import React, { useContext, useEffect, useMemo, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import API from '../../../services/API'
import {
  Autocomplete,
  Avatar,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Info from '@mui/icons-material/Info'
import DeleteIcon from '@mui/icons-material/Delete'
import PCOptions from '../../patientRecords/ePCR/History/presentingComplaintOptions'
import FastTrackDefaultOptions from '../../patientRecords/ePCR/History/FastTrackOptions'
import fastTrackPresentingComplaints from '../../patientRecords/ePCR/History/FastTrackOptions'
import CriticalCareSettings from './CriticalCareSettings'
import {
  EPCR_FORM_TYPES,
  getEnabledRecordTypeValues,
} from '../../patientRecords/ePCR/formTypes'

const complaintCategoryColors = {
  'Trauma & Injuries': 'error',
  Cardiorespiratory: 'primary',
  Neurological: 'secondary',
  Medical: 'success',
  'Mental Health': 'warning',
  'Obstetric & Gynaecological': 'info',
  'ENT / Eye / Skin': 'default',
  Other: 'default',
}

const DEFAULT_MODALITIES = [
  { key: 'xray', label: 'X-ray', ionising: true },
  { key: 'ultrasound', label: 'Ultrasound', ionising: false },
]

const slugify = (label) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const PatientRecords = () => {
  const settings = useContext(TenantSettingsContext).orgConfig?.orgSettings
  const [patientRecordSettings, setPatientRecordSettings] = useState({})
  const [staffOptions, setStaffOptions] = useState([])
  const [criticalCareSettings, setCriticalCareSettings] = useState({})
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  useEffect(() => {
    API.post('/users-in-team', {}).then((res) => {
      if (res.status === 200) setStaffOptions(res.data)
    })
  }, [])

  useEffect(() => {
    setPatientRecordSettings((prev) => ({
      ...prev,
      ...settings?.patientRecordSettings,
    }))
    setCriticalCareSettings(settings?.criticalCareSettings ?? {})
  }, [settings])

  const enabledRecordTypes = getEnabledRecordTypeValues({
    patientRecordSettings,
  })
  const criticalCareEnabled = enabledRecordTypes.includes('Critical Care')

  const toggleRecordType = (value, checked) =>
    setPatientRecordSettings((prev) => {
      const current = getEnabledRecordTypeValues({
        patientRecordSettings: prev,
      })
      const next = checked
        ? [...current, value]
        : current.filter((type) => type !== value)
      return {
        ...prev,
        // Keep the stored order stable and the full ePCR always on
        enabledRecordTypes: EPCR_FORM_TYPES.map((type) => type.value).filter(
          (type) => type === 'ePCR' || next.includes(type),
        ),
      }
    })

  // The fast-track list is select:false, so it arrives separately. Saving
  // replaces the whole patientRecordSettings object - block Save until it
  // has loaded or a custom list would be wiped.
  const [fastTrackLoaded, setFastTrackLoaded] = useState(false)
  useEffect(() => {
    API.get(
      '/settings?select=patientRecordSettings.fastTrackPresentingComplaints',
    ).then((res) => {
      if (res.status === 200) {
        setFastTrackLoaded(true)
        if (
          res?.data?.patientRecordSettings?.fastTrackPresentingComplaints
            ?.length
        ) {
          setPatientRecordSettings((prev) => ({
            ...prev,
            fastTrackPresentingComplaints:
              res.data.patientRecordSettings.fastTrackPresentingComplaints,
          }))
        }
      }
    })
  }, [])

  const arraysEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false
    return a.every((item, index) => item === b[index])
  }

  const allFastTrackOptionsSelected =
    (
      patientRecordSettings?.fastTrackPresentingComplaints ??
      FastTrackDefaultOptions
    ).length === fastTrackPresentingComplaints.length

  const handleSaveChanges = (e) => {
    e.preventDefault()

    const selectedFastTrack =
      patientRecordSettings?.fastTrackPresentingComplaints ??
      FastTrackDefaultOptions

    const payload = {
      ...patientRecordSettings,
    }

    if (arraysEqual(selectedFastTrack, FastTrackDefaultOptions)) {
      delete payload.fastTrackPresentingComplaints
    }

    // Drop empty modality rows and make sure each has a unique, stable key -
    // requests snapshot the modality by key, so keys must survive label edits
    if (payload.imagingSettings?.modalities) {
      const seenKeys = new Set()
      payload.imagingSettings = {
        ...payload.imagingSettings,
        modalities: payload.imagingSettings.modalities
          .filter((modality) => modality.label?.trim())
          .map((modality) => {
            let key = modality.key || slugify(modality.label)
            while (seenKeys.has(key)) key = `${key}-2`
            seenKeys.add(key)
            return { ...modality, key, label: modality.label.trim() }
          }),
      }
    }

    const body = { patientRecordSettings: payload }
    if (criticalCareEnabled) {
      body.criticalCareSettings = {
        ...criticalCareSettings,
        quickDrugs: criticalCareSettings.quickDrugs
          ?.filter((drug) => drug.name?.trim())
          .map((drug) => ({
            name: drug.name.trim(),
            dose: drug.dose?.trim() ?? '',
            route: drug.route?.trim() ?? '',
            categories: drug.categories ?? [],
          })),
        skillCurrency: criticalCareSettings.skillCurrency
          ?.filter((target) => target.procedure)
          .map((target) => ({
            procedure: target.procedure,
            count: Number(target.count) || 0,
            periodDays: Number(target.periodDays) || 0,
          })),
      }
    }

    API.put('/site-config', body).then((res) => {
      if (res.status === 200) {
        setToast(true)
        // PUT returns the whole settings document - merge it into
        // orgSettings (not orgConfig) so the rest of the app sees it
        setOrgConfig((prev) => ({
          ...prev,
          orgSettings: {
            ...prev.orgSettings,
            ...res.data,
          },
        }))
      }
    })
  }

  const presentingComplaintOptions = useMemo(() => {
    return Object.entries(PCOptions)
      .flatMap(([category, items]) =>
        items.map((x) =>
          typeof x === 'string'
            ? { category, label: x, keywords: [] }
            : { category, label: x.label, keywords: x.keywords || [] },
        ),
      )
      .sort((a, b) => {
        const categoryCompare = a.category.localeCompare(b.category)
        if (categoryCompare !== 0) return categoryCompare
        return a.label.localeCompare(b.label)
      })
  }, [])

  const presentingComplaintMap = useMemo(() => {
    return new Map(
      presentingComplaintOptions.map((option) => [option.label, option]),
    )
  }, [presentingComplaintOptions])

  const imagingSettings = patientRecordSettings?.imagingSettings ?? {}
  const imagingModalities = imagingSettings.modalities ?? DEFAULT_MODALITIES
  const imagingApproverIds = (imagingSettings.approvers ?? []).map(String)

  const updateImaging = (patch) =>
    setPatientRecordSettings((prev) => ({
      ...prev,
      imagingSettings: {
        ...(prev.imagingSettings ?? { modalities: DEFAULT_MODALITIES }),
        ...patch,
      },
    }))

  const updateModality = (index, patch) =>
    updateImaging({
      modalities: imagingModalities.map((modality, i) =>
        i === index ? { ...modality, ...patch } : modality,
      ),
    })

  const selectedAuditPresentingComplaints = useMemo(() => {
    const labels = patientRecordSettings?.auditPresentingComplaints ?? []
    return labels
      .map((label) => presentingComplaintMap.get(label))
      .filter(Boolean)
  }, [patientRecordSettings?.auditPresentingComplaints, presentingComplaintMap])

  return (
    <Stack gap={1} component="form" onSubmit={handleSaveChanges}>
      <Typography variant="title2">Patient Records Configuration</Typography>
      <Stack gap={1}>
        <Typography variant="title3">Record types</Typography>
        <Typography variant="caption">
          Choose which record types crews can create. Existing records of a
          type you switch off still open as normal.
        </Typography>
        {EPCR_FORM_TYPES.map((type) => (
          <FormControlLabel
            key={type.value}
            label={
              <Stack>
                <Typography fontWeight={600}>
                  {type.label}
                  {type.value === 'ePCR' && ' (always on)'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {type.description}
                </Typography>
              </Stack>
            }
            sx={{ alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.5 } }}
            control={
              <Switch
                checked={enabledRecordTypes.includes(type.value)}
                disabled={type.value === 'ePCR'}
                onChange={(e) =>
                  toggleRecordType(type.value, e.target.checked)
                }
                color="success"
              />
            }
          />
        ))}
      </Stack>
      {criticalCareEnabled && (
        <>
          <Divider />
          <CriticalCareSettings
            value={criticalCareSettings}
            onChange={setCriticalCareSettings}
          />
        </>
      )}
      <Divider />
      <FormControlLabel
        label={
          <Stack direction="row" gap={1} alignItems="center">
            <>Disable Medicines Administration Form outside of the ePCR</>
            <Tooltip title="This will prevent staff from recording drug administrations outside of the EPCR system.">
              <Info fontSize="small" color="info" />
            </Tooltip>
          </Stack>
        }
        control={
          <Checkbox
            checked={
              patientRecordSettings?.disabledMedicinesAdministrationForm ??
              false
            }
            onChange={(e) =>
              setPatientRecordSettings((prev) => ({
                ...prev,
                disabledMedicinesAdministrationForm: e.target.checked,
              }))
            }
            color="error"
          />
        }
      />
      <Divider />
      <Stack gap={1}>
        <Typography variant="title3">
          Fast Track Presenting Complaints
        </Typography>
        <Typography variant="caption">
          Control the list of allowed presenting complaints for the{' '}
          <strong>Fast Track</strong> patient care record
        </Typography>
        <Autocomplete
          options={fastTrackPresentingComplaints}
          value={
            patientRecordSettings?.fastTrackPresentingComplaints ??
            FastTrackDefaultOptions
          }
          popupIcon={allFastTrackOptionsSelected ? null : undefined}
          multiple
          disableClearable
          onChange={(e, value) =>
            setPatientRecordSettings((prev) => ({
              ...prev,
              fastTrackPresentingComplaints: value,
            }))
          }
          fullWidth
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Stack>
      <Divider />
      <Stack gap={1}>
        <Typography variant="title3">Audits</Typography>
        <Typography variant="caption">
          Records are checked against these criteria when they are submitted.
          Matching records appear in <strong>Patient Records → Record Audits</strong>
        </Typography>
        <FormControlLabel
          label="Audit all ePCRs"
          control={
            <Checkbox
              checked={patientRecordSettings?.auditAllRecords ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditAllRecords: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <Stack direction="row" overflow="auto" alignItems="center" gap={1}>
          <Typography
            color={patientRecordSettings?.auditAllRecords ? 'grey' : 'inherit'}
          >
            Record{' '}
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            value={patientRecordSettings?.auditMonthlyPercentage ?? 10}
            type="number"
            inputProps={{ min: 0, max: 100 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            disabled={patientRecordSettings?.auditAllRecords}
            onChange={(e) => {
              const value = Math.max(0, Math.min(100, Number(e.target.value)))
              setPatientRecordSettings((prev) => ({
                ...prev,
                auditMonthlyPercentage: Number.isNaN(value) ? 0 : value,
              }))
            }}
          />
          <Typography
            color={patientRecordSettings?.auditAllRecords ? 'grey' : 'inherit'}
          >
            of records every month.
          </Typography>
        </Stack>
        <Autocomplete
          disabled={patientRecordSettings?.auditAllRecords}
          multiple
          options={presentingComplaintOptions}
          groupBy={(o) => o.category}
          getOptionLabel={(o) => o.label || ''}
          value={selectedAuditPresentingComplaints}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          onChange={(e, value) => {
            const cleaned = value.map((v) => v.label).filter(Boolean)
            setPatientRecordSettings((prev) => ({
              ...prev,
              auditPresentingComplaints: cleaned,
            }))
          }}
          renderTags={(value, getTagProps) => (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {value.map((tag, index) => {
                const { key, ...chipProps } = getTagProps({ index })
                return (
                  <Chip
                    key={key}
                    label={tag.label}
                    color={complaintCategoryColors[tag.category] || 'default'}
                    sx={{ borderRadius: 1 }}
                    {...chipProps}
                  />
                )
              })}
            </Stack>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Audit all records with these presenting complaints"
            />
          )}
        />
        <FormControlLabel
          label="Audit all ePCRs from 'New Starter' employees"
          disabled={patientRecordSettings?.auditAllRecords}
          control={
            <Checkbox
              checked={patientRecordSettings?.auditNewStarters ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditNewStarters: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <FormControlLabel
          label="Audit all ePCRs of paediatric patients"
          disabled={patientRecordSettings?.auditAllRecords}
          control={
            <Checkbox
              checked={patientRecordSettings?.auditPaediatricPatients ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditPaediatricPatients: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <FormControlLabel
          label="Audit all ePCRs of patients discharged on scene"
          disabled={patientRecordSettings?.auditAllRecords}
          control={
            <Checkbox
              checked={patientRecordSettings?.auditDischargedPatients ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditDischargedPatients: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <FormControlLabel
          label="Audit all ePCRs of patients transferred to hospital"
          disabled={patientRecordSettings?.auditAllRecords}
          control={
            <Checkbox
              checked={patientRecordSettings?.auditTransportedPatients ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditTransportedPatients: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <FormControlLabel
          label="Audit all ePCRs including a drug administration"
          disabled={patientRecordSettings?.auditAllRecords}
          control={
            <Checkbox
              checked={patientRecordSettings?.auditDrugAdministrations ?? false}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  auditDrugAdministrations: e.target.checked,
                }))
              }
              color="success"
            />
          }
        />
        <FormControlLabel
          label={
            <Stack direction="row" gap={1} alignItems="center">
              <>Prevent staff involved in a record from completing its audit</>
              <Tooltip title="Anyone recorded on the ePCR - the author, a past owner, or listed crew - will be blocked from completing that record's audit. When disabled they will still see a warning.">
                <Info fontSize="small" color="info" />
              </Tooltip>
            </Stack>
          }
          control={
            <Checkbox
              checked={patientRecordSettings?.preventSelfAudit ?? true}
              onChange={(e) =>
                setPatientRecordSettings((prev) => ({
                  ...prev,
                  preventSelfAudit: e.target.checked,
                }))
              }
              color="error"
            />
          }
        />
      </Stack>
      <Divider />
      <Stack gap={1}>
        <Typography variant="title3">Imaging Studies</Typography>
        <Typography variant="caption">
          Let clinicians request imaging studies (X-ray, ultrasound) from the
          ePCR <strong>Assess</strong> tab, with an approval step and radiation
          dose recording for ionising modalities
        </Typography>
        <FormControlLabel
          label="Enable imaging requests in the ePCR"
          control={
            <Checkbox
              checked={imagingSettings.enabled ?? false}
              onChange={(e) => updateImaging({ enabled: e.target.checked })}
              color="success"
            />
          }
        />
        {(imagingSettings.enabled ?? false) && (
          <Stack gap={1}>
            <FormControlLabel
              label={
                <Stack direction="row" gap={1} alignItems="center">
                  <>Require approval before a study can be performed</>
                  <Tooltip title="When enabled, requests wait for one of the imaging approvers below before images and a clinical impression can be recorded. When disabled, studies are approved automatically on request.">
                    <Info fontSize="small" color="info" />
                  </Tooltip>
                </Stack>
              }
              control={
                <Checkbox
                  checked={imagingSettings.requireApproval ?? true}
                  onChange={(e) =>
                    updateImaging({ requireApproval: e.target.checked })
                  }
                  color="error"
                />
              }
            />
            <Autocomplete
              multiple
              options={staffOptions}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              isOptionEqualToValue={(a, b) => a._id === b._id}
              value={staffOptions.filter((option) =>
                imagingApproverIds.includes(String(option._id)),
              )}
              onChange={(e, value) =>
                updateImaging({ approvers: value.map((option) => option._id) })
              }
              renderTags={(value, getTagProps) => (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index })
                    return (
                      <Chip
                        key={key}
                        avatar={<Avatar src={option.photograph} />}
                        label={`${option.firstName} ${option.lastName}`}
                        sx={{ borderRadius: 1 }}
                        {...chipProps}
                      />
                    )
                  })}
                </Stack>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Imaging Approvers"
                  helperText="Who can approve or reject imaging requests"
                />
              )}
            />
            <Typography variant="caption">
              Modalities clinicians can request. Ionising modalities require a
              pregnancy status before approval and can have a radiation dose
              recorded against each study.
            </Typography>
            {imagingModalities.map((modality, index) => (
              <Stack
                key={index}
                direction="row"
                gap={1}
                alignItems="center"
              >
                <TextField
                  size="small"
                  label="Modality"
                  value={modality.label ?? ''}
                  onChange={(e) =>
                    updateModality(index, { label: e.target.value })
                  }
                  fullWidth
                />
                <FormControlLabel
                  label="Ionising"
                  control={
                    <Checkbox
                      checked={modality.ionising ?? false}
                      onChange={(e) =>
                        updateModality(index, { ionising: e.target.checked })
                      }
                      color="warning"
                    />
                  }
                />
                <IconButton
                  color="error"
                  onClick={() =>
                    updateImaging({
                      modalities: imagingModalities.filter(
                        (x, i) => i !== index,
                      ),
                    })
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              size="small"
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() =>
                updateImaging({
                  modalities: [
                    ...imagingModalities,
                    { key: '', label: '', ionising: false },
                  ],
                })
              }
            >
              Add Modality
            </Button>
          </Stack>
        )}
      </Stack>

      <Button
        color="success"
        type="submit"
        variant="contained"
        size="small"
        fullWidth
        disabled={!fastTrackLoaded}
      >
        Save Changes
      </Button>
    </Stack>
  )
}

export default PatientRecords
